import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useContext,
	useCallback,
} from "react";
import { createPortal } from "react-dom";
import { format, isSameDay, subDays } from "date-fns";
import {
	MessageCircle,
	X,
	Send,
	Paperclip,
	Image as ImageIcon,
	FileText,
	FileIcon,
	FileArchive,
	FileSpreadsheet,
	Eye,
	Download,
	Search,
	Minus,
	ChevronUp,
	ChevronDown,
	Reply,
	Smile,
	Mic,
	Check,
	Sticker,
	Sparkles,
} from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../ui/popover";
import { AvatarUsuario, WHATSAPP_LIGHT, WHATSAPP_DARK, corPontoPresenca, rotuloPresenca, getInitials } from "./shared.jsx";
import { useThemeContext } from "../../context/DarkMode";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { getPublicFileUrl } from "../../config";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";
import MiniaturaDePdf from "../MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
	tocarSomNovoChatInterno,
	tocarSomEnviadoChatInterno,
	getChatInternoNotifVolume01,
} from "../../services/chatInternoSom";
import { cn } from "../../lib/utils";
import RecordingTimer from "../MessageInput/RecordingTimer";
import { paraWav } from "../MessageInput/wav";
import FigurinhaPicker from "../FigurinhaPicker";
import AssistenteDeTexto from "../MessageInput/AssistenteDeTexto";

const CHAT_W = 380;
const CHAT_H = 540;

const FORMATOS_AUDIO_W = [
	{ mime: "audio/ogg;codecs=opus", ext: "ogg" },
	{ mime: "audio/webm;codecs=opus", ext: "webm" },
	{ mime: "audio/mp4", ext: "m4a" },
	{ mime: "audio/webm", ext: "webm" }
];

const escolherFormatoAudioW = () => {
	if (typeof MediaRecorder === "undefined") return null;
	return (
		FORMATOS_AUDIO_W.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
			mime: "",
			ext: "webm",
		}
	);
};

// #region debug-point popup-widget
const DBG_KEY_W = "dbg_internal_chat_widget_v1";
const dbgPushW = (ev) => {
	try {
		const raw = localStorage.getItem(DBG_KEY_W) || "[]";
		const arr = JSON.parse(raw);
		arr.push({ t: Date.now(), ...ev });
		while (arr.length > 1500) arr.shift();
		localStorage.setItem(DBG_KEY_W, JSON.stringify(arr));
	} catch (_) {}
};
if (typeof window !== "undefined") {
	try {
		const raw = localStorage.getItem(DBG_KEY_W);
		if (!raw) localStorage.setItem(DBG_KEY_W, JSON.stringify([]));
	} catch (_) {}
}
// #endregion

// =====================================================
// HELPERS: dia / escape regex / highlight
// =====================================================
const chaveDia = (isoDate) => {
	if (!isoDate) return "";
	try {
		return format(new Date(isoDate), "yyyy-MM-dd");
	} catch {
		return "";
	}
};

const rotuloDia = (chave) => {
	if (!chave) return "";
	try {
		const [y, m, d] = chave.split("-").map(Number);
		const data = new Date(y, m - 1, d, 12, 0, 0);
		const hoje = new Date();
		const ontem = subDays(hoje, 1);
		if (isSameDay(data, hoje)) return "Hoje";
		if (isSameDay(data, ontem)) return "Ontem";
		return format(data, "dd/MM/yyyy");
	} catch {
		return chave;
	}
};

const escapeRegex = (s) =>
	String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const destacarTexto = (texto, busca, destacar) => {
	const t = String(texto == null ? "" : texto);
	const b = String(busca || "").trim();
	if (!b || !t) return t;
	const needles = [];
	const lowerT = t.toLowerCase();
	const lowerB = b.toLowerCase();
	let cursor = 0;
	while (cursor < t.length) {
		const found = lowerT.indexOf(lowerB, cursor);
		if (found < 0) break;
		needles.push({ start: found, end: found + b.length });
		cursor = found + b.length;
	}
	if (needles.length === 0) return t;
	const nodes = [];
	let anterior = 0;
	needles.forEach((r, i) => {
		if (r.start > anterior) nodes.push(t.slice(anterior, r.start));
		nodes.push(
			<mark
				key={i}
				style={{
					backgroundColor: destacar ? "#ffcc4d" : "#fff3a3",
					color: "#111b21",
					borderRadius: 2,
					padding: "0 1px",
				}}
			>
				{t.slice(r.start, r.end)}
			</mark>
		);
		anterior = r.end;
	});
	if (anterior < t.length) nodes.push(t.slice(anterior));
	return nodes;
};

const tipoDeMidia = (nomeArquivoOuMime) => {
	if (!nomeArquivoOuMime) return "application";
	const mime =
		typeof nomeArquivoOuMime === "string" && nomeArquivoOuMime.includes("/")
			? nomeArquivoOuMime
			: "";
	const ext =
		mime ||
		extensaoDe(String(nomeArquivoOuMime || "")).replace(/^\./, "");
	if (mime.startsWith("image/") || /jpe?g|png|gif|webp|svg|bmp/.test(ext))
		return "image";
	if (mime.startsWith("video/") || /mp4|webm|mov|mkv/.test(ext)) return "video";
	if (mime.startsWith("audio/") || /mp3|wav|ogg|opus|m4a/.test(ext)) return "audio";
	return "application";
};

const iconeDoAnexo = (tipo, className = "h-5 w-5") => {
	switch (tipo) {
		case "image":
			return <ImageIcon className={className} />;
		case "video":
			return <ImageIcon className={className} />;
		case "audio":
			return <FileText className={className} />;
		case "application":
		default:
			return <FileText className={className} />;
	}
};

const EyeIcon = ({ className = "h-5 w-5" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
		<circle cx="12" cy="12" r="3" />
	</svg>
);

const DownloadArrowIcon = ({ className = "h-5 w-5" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="7 10 12 15 17 10" />
		<line x1="12" x2="12" y1="15" y2="3" />
	</svg>
);

const CopyIcon = ({ className = "h-4 w-4" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
		<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
	</svg>
);

const RefreshIcon = ({ className = "h-4 w-4" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
		<path d="M21 3v5h-5" />
	</svg>
);

const DownloadIcon = ({ className = "h-6 w-6" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="7 10 12 15 17 10" />
		<line x1="12" x2="12" y1="15" y2="3" />
	</svg>
);

const PLANILHAS_W = [".xls", ".xlsx", ".csv", ".ods"];
const COMPACTADOS_W = [".zip", ".rar", ".7z", ".gz", ".tgz"];

const IconeDoDocumentoW = ({ extensao }) => {
	if (PLANILHAS_W.includes(extensao))
		return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
	if (COMPACTADOS_W.includes(extensao))
		return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
	return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
};

const RenderizarMidiaMin = ({ mensagem, tema, darkMode, onAbrirImagem, abrirArquivo, transcrevendo, transcription, transcribedAt, onTranscrever }) => {
	if (!mensagem?.mediaUrl) return null;
	const url = getPublicFileUrl(mensagem.mediaUrl);
	const tipo = mensagem.mediaType || tipoDeMidia(mensagem.mediaUrl);
	const nome = decodeURIComponent(
		String(mensagem.mediaUrl || "").split("/").pop() || "Arquivo"
	);
	const ext = extensaoDe(mensagem.mediaUrl).replace(/^\./, "").toUpperCase() || "ARQUIVO";

	if (tipo === "sticker") {
		// IGUAL ao MessagesList/index.jsx de tickets (L1729-L1769):
		//   • tamanho fixo 150x150px
		//   • object-contain (preserva aspect ratio da figurinha original)
		//   • SEM botão de zoom / onClick capturando (só desenha, não abre modal)
		//   • draggable nativo para poder arrastar/salvar com direito
		if (!url) {
			return (
				<div
					className="mb-1 flex h-[150px] w-[150px] items-center justify-center rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
					title="Figurinha não carregada"
				>
					Figurinha
				</div>
			);
		}
		return (
			<div className="mb-1" style={{ background: "transparent" }}>
				<img
					src={url}
					alt={nome}
					loading="eager"
					decoding="async"
					draggable
					onContextMenu={(e) => {
						try { e.stopPropagation(); } catch {}
					}}
					style={{ WebkitUserDrag: "auto", userSelect: "auto" }}
					className="h-[150px] w-[150px] object-contain"
				/>
			</div>
		);
	}

	if (tipo === "image") {
		return (
			<button
				type="button"
				onClick={(e) => {
					e.preventDefault();
					onAbrirImagem?.(url, nome);
				}}
				className="mb-1 block w-full overflow-hidden rounded-lg text-left"
				style={{
					background: "transparent",
					border: 0,
					padding: 0,
					cursor: "zoom-in",
				}}
			>
				<img
					src={url}
					alt={nome}
					loading="lazy"
					className="max-h-60 w-full rounded-lg object-cover"
				/>
			</button>
		);
	}

	if (tipo === "video") {
		return (
			<video
				src={url}
				controls
				preload="metadata"
				className="mb-1 max-h-64 w-full rounded-lg bg-black"
			/>
		);
	}

	if (tipo === "audio") {
		const temTranscricao =
			typeof transcription === "string" && transcription.trim() !== "";
		const handleCopiarTranscricao = async () => {
			if (!transcription) return;
			try {
				await navigator.clipboard.writeText(transcription);
			} catch (_) {}
		};
		const handleRefazerTranscricao = () => {
			onTranscrever?.({ forcar: true });
		};
		return (
			<div className="mb-1">
				<div
					className="flex w-full items-center gap-2 rounded-lg p-2"
					style={{
						backgroundColor: darkMode
							? "rgba(255,255,255,0.06)"
							: "rgba(0,0,0,0.05)",
					}}
				>
					<FileText className="h-5 w-5 shrink-0" style={{ color: tema.textoMuted }} />
					<audio
						src={url}
						controls
						preload="metadata"
						className="flex-1 min-w-0"
					/>
				</div>
				{temTranscricao ? (
					<div
						className="mt-2 w-full rounded-lg border overflow-hidden"
						style={{
							backgroundColor: darkMode ? "rgba(255,255,255,0.03)" : "#ffffff",
							borderColor: tema.borderSeparator,
						}}
					>
						<div
							className="flex items-center justify-between px-3 pt-2.5 pb-2"
							style={{
								backgroundColor: darkMode ? "rgba(255,255,255,0.02)" : "#fafafa",
								borderBottom: `1px solid ${tema.borderSeparator}`,
							}}
						>
							<div
								className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider"
								style={{ color: "#8696a0" }}
							>
								<CopyIcon className="h-3.5 w-3.5" style={{ strokeWidth: 2.2 }} />
								Transcrição
							</div>
							<div className="flex items-center gap-1.5">
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleCopiarTranscricao();
									}}
									title="Copiar transcrição"
									className="flex h-7 w-7 items-center justify-center rounded-full transition-colors"
									style={{ color: "#8696a0" }}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)")}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
								>
									<CopyIcon className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleRefazerTranscricao();
									}}
									title="Transcrever novamente"
									className="flex h-7 w-7 items-center justify-center rounded-full transition-colors"
									style={{ color: "#8696a0" }}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)")}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
								>
									<RefreshIcon className="h-4 w-4" />
								</button>
							</div>
						</div>
						<div
							className="px-3 py-3 text-[14px] leading-relaxed whitespace-pre-wrap"
							style={{
								color: darkMode ? "#e9edef" : "#111b21",
							}}
						>
							{transcription}
						</div>
					</div>
				) : transcrevendo ? (
					<div
						className="mt-2 inline-flex items-center gap-2 rounded-md px-1 py-1 text-[14px]"
						style={{ color: "#8696a0" }}
					>
						⏳ Transcrevendo...
					</div>
				) : transcribedAt ? null : (
					<button
						type="button"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							onTranscrever?.();
						}}
						className="mt-2 inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[14px] font-normal transition-colors"
						style={{
							backgroundColor: "transparent",
							border: "none",
							color: "#54656f",
							cursor: "pointer",
						}}
					>
						<CopyIcon className="h-4 w-4" style={{ strokeWidth: 2 }} />
						Transcrever
					</button>
				)}
			</div>
		);
	}

	const urlMedia = url;
	const extensaoLower = extensaoDe(mensagem.mediaUrl);
	const rotulo = extensaoLower
		? `Arquivo ${extensaoLower.replace(".", "").toUpperCase()}`
		: "Arquivo";

	return (
		<div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
			{extensaoLower === ".pdf" && (
				<div className="mb-2">
					<MiniaturaDePdf
						url={urlMedia}
						onAbrir={() => abrirArquivo?.(mensagem.id)}
					/>
				</div>
			)}

			<div className="flex items-center gap-2">
				<IconeDoDocumentoW extensao={extensaoLower} />
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium">{rotulo}</p>
					<p className="text-xs text-muted-foreground">
						{extensaoLower ? extensaoLower.replace(".", "").toUpperCase() : "documento"}
					</p>
				</div>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 shrink-0"
					title="Visualizar"
					onClick={() => abrirArquivo?.(mensagem.id)}
				>
					<Eye className="h-4 w-4" />
				</Button>
				<Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0">
					<a
						href={urlMedia}
						target="_blank"
						rel="noopener noreferrer"
						title="Baixar"
					>
						<Download className="h-4 w-4" />
					</a>
				</Button>
			</div>
		</div>
	);
};

// ================================================================
// ITEM 6.1 — deduplicação e cancelamento de conversas no widget.
// Mesmo pattern de pages/InternalChat L850-L1177.
// Acontecia em uso multi-abas + socket events baterem ao mesmo tempo:
//   1. 2x socket onMessage / conversationUpdate / read / group chegam
//      quase no mesmo ms.
//   2. Cada um dispara api.get("/internal-chat/conversations") independente.
//   3. Respostas voltam FORA DE ORDEM (race).
//   4. Segundo request que chegou processa com totalUnread antigo, grava
//      estado com valor obsoleto. Unread do topo flutua. Unread da
//      conversa aberta some.
// Solução 2 camadas:
//   (A) AbortController singleton — requisição NOVO aborta a ANTERIOR,
//       o then da antiga cai em catch (abort) e NÃO toca estado.
//   (B) Map dedup por conversationKey() — MESMA conversa 2x no payload
//       (join errado backend) não duplica contagem.
// ================================================================
const conversationKeyW = (conv) => {
	if (!conv || typeof conv !== "object") return null;
	const tipo = String(conv.type || "");
	if (tipo === "group") {
		const gid = Number(conv.groupId);
		return Number.isFinite(gid) && gid > 0 ? `group-${gid}` : null;
	}
	// padrão = direct
	const uid = Number(conv.userId);
	return Number.isFinite(uid) && uid > 0 ? `direct-${uid}` : null;
};

export default function InternalChatWidget() {
	const { darkMode } = useThemeContext();
	const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;
	const { user } = useContext(AuthContext);

	const [popoverAberto, setPopoverAberto] = useState(false);
	const [busca, setBusca] = useState("");
	const [equipe, setEquipe] = useState([]);
	const [carregandoEquipe, setCarregandoEquipe] = useState(false);
	const [totalUnread, setTotalUnread] = useState(0);

	const [chatAbertoCom, setChatAbertoCom] = useState(null); // user ou null
	const [chatEstado, setChatEstado] = useState("aberto"); // "aberto" | "minimizado"
	const [mensagens, setMensagens] = useState([]);
	const [carregandoMsgs, setCarregandoMsgs] = useState(false);
	const [texto, setTexto] = useState("");
	const [anexos, setAnexos] = useState([]); // [{file, preview}]
	const [enviando, setEnviando] = useState(false);
	const [dragOver, setDragOver] = useState(false);
	const [gravando, setGravando] = useState(false);

	// (B) notificação minimizado
	const [piscandoMinimizado, setPiscandoMinimizado] = useState(false);
	const [novasCount, setNovasCount] = useState(0);

	// Buffer de mensagens recebidas quando a conversa ainda não estava aberta
	// (evita perder mensagens em tempo real enquanto usuário não clica em abrir).
	// Usamos useRef de propósito: não precisamos re-renderizar o widget a cada
	// mensagem que chega em uma conversa fechada — só precisamos do dado em
	// memória para quando o usuário abrir a conversa depois.
	const bufferMsgsPorUsuarioRef = useRef({});

	// (E) barra de busca por mensagens
	const [buscaAberta, setBuscaAberta] = useState(false);
	const [buscaMsg, setBuscaMsg] = useState("");
	const [indiceMatch, setIndiceMatch] = useState(0);

	const inputRef = useRef(null);
	const buscaMsgInputRef = useRef(null);
	const fileRef = useRef(null);
	const msgsEndRef = useRef(null);
	const gravadorRef = useRef(null);
	const pedacosRef = useRef([]);
	const mensagensScrollRef = useRef(null);
	const mensagensListaRef = useRef(null);
	const socketRef = useRef(null);
	const userRef = useRef(null);
	const chatContainerRef = useRef(null);
	// Refs de estado para leitura síncrona em listeners de socket
	// (evita anti-padrão React 18: setar variável externa DENTRO de
	// setState updater e ler FORA — updater roda assíncrono/agendado,
	// variável sempre tem valor antigo no momento da leitura).
	const chatAbertoComRef = useRef(null);
	const chatEstadoRef = useRef("aberto");
	// Cancelamento de requisições concorrentes de listagem de conversas.
	// Última requisição iniciada SEMPRE vence. Evita race "resposta velha
	// chega depois e pisa estado novo".
	const fetchConversasAbortRef = useRef(null);

	// ============================================================
	// (F) Reações popup + Responder por clique duplo (mesma UX da página)
	// ============================================================
	const [reactionPopoverFor, setReactionPopoverFor] = useState(null);
	const [quotedMessage, setQuotedMessage] = useState(null);
	const [imagemModalWidget, setImagemModalWidget] = useState(null); // { url, nome } | null
	const [visualizandoW, setVisualizandoW] = useState(null); // índice em arquivosDaConversaW | null
	const [transcrevendoIdsW, setTranscrevendoIdsW] = useState([]); // ids sendo transcritos

	// ================================================================
	// (G) Figurinhas (igual MessageInput do ticket) + botão IA
	// ================================================================
	const [figurinhaAberto, setFigurinhaAberto] = useState(false);
	const figurinhaAncoraRef = useRef(null);

	// Função de envio customizada do InternalChat para figurinhas.
	// IGUAL ao envio de figurinha de TICKETS (FigurinhaPicker fluxo original,
	// que faz POST /figurinhas/:id/enviar com {ticketId, quotedMsg}), só que
	// usa a rota DEDICADA do backend para chat interno:
	//   POST /internal-chat/direct/:userId/figurinhas/:id/enviar  (direta)
	//   POST /internal-chat/groups/:id/figurinhas/:itemId/enviar  (grupo)
	//
	// Isso NÃO envolve fetch blob, conversão para File, upload multipart,
	// CORS same-origin hacks, nada — o backend COPIA a figurinha salva e grava
	// direto na InternalChatMessage, 100x mais simples e sem falhas em produção.
	//
	// Retorno: true em sucesso, false em erro (igual ao fluxo de tickets).
	// Mesmo se erro retornar false, o FigurinhaPicker commit a630f93 NÃO cai
	// mais em fallback de rota tickets (return no bloco custom).
	const enviarFigurinhaChatInterno = useCallback(
		async (figurinha, ctx) => {
			if (!chatAbertoCom || !figurinha?.id) return false;
			try {
				setEnviando(true);
				const ehGrupo = chatAbertoCom.type === "group";
				const rota = ehGrupo
					? `/internal-chat/groups/${chatAbertoCom.id}/figurinhas/${figurinha.id}/enviar`
					: `/internal-chat/direct/${chatAbertoCom.id}/figurinhas/${figurinha.id}/enviar`;

				const quotedMsg = ctx?.quotedMsg || quotedMessage || null;
				const quotedMsgId =
					quotedMsg && quotedMsg.id ? String(quotedMsg.id) : undefined;

				const tempId = `tmp_${Date.now()}`;
				const temporaria = {
					__tempId: tempId,
					id: -Math.floor(Math.random() * 1e9),
					body: "",
					mediaUrl: getPublicFileUrl(figurinha.mediaUrl) || "",
					mediaType: "sticker",
					fromUserId: Number(user?.id),
					toUserId: ehGrupo ? null : Number(chatAbertoCom.id),
					groupId: ehGrupo ? Number(chatAbertoCom.id) : null,
					createdAt: new Date().toISOString(),
					fromUser: {
						id: Number(user?.id),
						name: user?.name || "Eu",
						profilePicUrl: user?.profilePicUrl || null,
					},
					quotedMsg:
						quotedMsg && quotedMsg.id
							? {
									id: quotedMsg.id,
									fromUserId: quotedMsg.fromUserId,
									fromUser: quotedMsg.fromUser,
									body: quotedMsg.body,
									mediaUrl: quotedMsg.mediaUrl,
									mediaType: quotedMsg.mediaType,
								}
							: null,
				};
				setMensagens((m) => [...m, temporaria]);
				msgsEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
				tocarSomEnviadoChatInterno();

				const payload = {};
				if (quotedMsgId !== undefined) payload.quotedMsgId = quotedMsgId;

				const { data } = await api.post(rota, payload);
				const enviadas = Array.isArray(data) ? data : [data];
				setMensagens((m) => {
					const semTemp = m.filter((x) => x.__tempId !== tempId);
					return [...semTemp, ...enviadas];
				});
				setQuotedMessage(null);
				return true;
			} catch (err) {
				toastError(err);
				return false;
			} finally {
				setEnviando(false);
				requestAnimationFrame(() => inputRef.current?.focus?.());
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[chatAbertoCom?.id, chatAbertoCom?.type, quotedMessage, user]
	);

	const reactionPopoverForRef = useRef(null);
	const messagesRef = useRef([]);
	const popupHostRef = useRef(null);
	const listenerNativeInstaladoRef = useRef(false);

	const balaoRefs = useRef(new Map());
	const balaoRefsSet = (msgId, value) => {
		try {
			if (value == null) {
				balaoRefs.current.delete(msgId);
			} else {
				balaoRefs.current.set(msgId, value);
			}
		} catch { /* no-op */ }
	};
	const msgClickState = useRef({ lastTs: 0, lastId: null });

	const replyToMessageRef = useRef(() => {});

	// Sync refs com estados (leitura síncrona em listeners nativos e socket).
	useEffect(() => {
		reactionPopoverForRef.current = reactionPopoverFor;
	}, [reactionPopoverFor]);
	useEffect(() => {
		messagesRef.current = mensagens;
	}, [mensagens]);

	const arquivosDaConversaW = mensagens
		.filter((m) => m.mediaUrl && !m.isDeleted)
		.map((m) => ({
			id: m.id,
			url: getPublicFileUrl(m.mediaUrl),
			mediaType: m.mediaType,
			legenda: m.body || "",
			fromMe: m.fromMe,
			createdAt: m.createdAt,
		}));

	const abrirArquivoW = (messageId) => {
		const indice = arquivosDaConversaW.findIndex((a) => a.id === messageId);
		if (indice >= 0) setVisualizandoW(indice);
	};

	const cancelReply = () => setQuotedMessage(null);
	const fecharPopupReacoes = useCallback(() => {
		reactionPopoverForRef.current = null;
		setReactionPopoverFor(null);
	}, []);
	const replyToMessage = useCallback((message) => {
		if (!message) return;
		setQuotedMessage(message);
		fecharPopupReacoes();
		// Focar input 50ms após (esperar paint do quoted preview).
		setTimeout(() => {
			try {
				inputRef.current?.focus?.();
			} catch { /* no-op */ }
		}, 50);
	}, [fecharPopupReacoes]);
	useEffect(() => {
		replyToMessageRef.current = replyToMessage;
	}, [replyToMessage]);

	const handleTranscreverW = useCallback(async (m, opts = {}) => {
		const { forcar = false } = opts;
		if (!m?.id) return;
		const id = +m.id;
		setTranscrevendoIdsW((prev) => (prev.includes(id) ? prev : [...prev, id]));
		try {
			const { data } = await api.post(`/internal-chat/messages/${id}/transcribe`, { forcar: !!forcar });
			setMensagens((prev) =>
				prev.map((x) =>
					+x.id === id
						? {
								...x,
								transcription:
									typeof data?.transcription === "string"
										? data.transcription
										: x.transcription ?? "",
								transcribedAt: data?.transcribedAt || x.transcribedAt || new Date().toISOString(),
							}
						: x
				)
			);
		} catch (err) {
			toastError(err);
		} finally {
			setTranscrevendoIdsW((prev) => prev.filter((x) => +x !== +id));
		}
	}, []);

	const toggleReaction = async (messageId, emoji) => {
		dbgPushW({ ev: "widget_toggleReaction_START", messageId: String(messageId), emoji });
		const mid = Number(messageId);
		let stateAnterior = null;
		try {
			if (!messageId || !emoji) return;
			stateAnterior = messagesRef.current.map(m => +m.id === mid ? { ...m, reacoes: Array.isArray(m.reacoes) ? m.reacoes.map(r=>({...r})) : [] } : m);
			const userIdAtual = userRef?.current?.id;
			const agregaReacoes = (reacoesExistentes) => {
				const arr = Array.isArray(reacoesExistentes) ? reacoesExistentes : [];
				const agrupado = new Map();
				arr.forEach((r) => {
					if (!r || !r.emoji) return;
					const e = String(r.emoji);
					const users = Array.isArray(r.userIds)
						? r.userIds.map(String)
						: r.userId != null
						? [String(r.userId)]
						: [];
					const atual = agrupado.get(e);
					if (atual) {
						users.forEach((u) => { if (!atual.userIds.includes(u)) atual.userIds.push(u); });
					} else {
						agrupado.set(e, { emoji: e, total: 0, userIds: users });
					}
				});
				const saida = [];
				agrupado.forEach((v, e) => { saida.push({ emoji: e, total: v.userIds.length, userIds: v.userIds }); });
				return saida;
			};
			const toggleAgg = (lista, emj, uid) => {
				const agr = agregaReacoes(lista);
				const idx = agr.findIndex((x) => x.emoji === emj);
				if (idx >= 0) {
					const linha = agr[idx];
					const jaTem = linha.userIds.includes(String(uid));
					if (jaTem) {
						linha.userIds = linha.userIds.filter((u) => u !== String(uid));
						linha.total = linha.userIds.length;
						if (linha.total <= 0) agr.splice(idx, 1);
					} else {
						linha.userIds.push(String(uid));
						linha.total = linha.userIds.length;
					}
				} else {
					agr.push({ emoji: emj, total: 1, userIds: [String(uid)] });
				}
				return agr.map((r)=>({...r, _t: Date.now()}));
			};
			if (userIdAtual != null) {
				setMensagens((prev) =>
					prev.map((m) =>
						+m.id === mid
							? { ...m, reacoes: toggleAgg(m.reacoes, emoji, userIdAtual) }
							: m
					)
				);
			}
			const { data } = await api.post(`/internal-chat/messages/${messageId}/reactions`, { emoji });
			dbgPushW({ ev: "widget_toggleReaction_SUCCESS", messageId: String(messageId), emoji, hasData: !!data });
			if (data && Array.isArray(data.reacoes)) {
				setMensagens((prev) =>
					prev.map((m) =>
						+m.id === mid
							? { ...m, reacoes: data.reacoes.map((r) => ({ ...r, _t: Date.now() })) }
							: m
					)
				);
			}
		} catch (err) {
			dbgPushW({ ev: "widget_toggleReaction_ERROR", messageId: String(messageId), emoji, msg: err?.message || String(err).slice(0, 180) });
			if (stateAnterior) setMensagens(() => stateAnterior);
		} finally {
			dbgPushW({ ev: "widget_toggleReaction_END_CLOSE_POPUP_NOW", mid: String(mid) });
			fecharPopupReacoes();
		}
	};

	// Listener nativo: 2x clique esquerdo = responder; 1x clique direito = abre popup reações.
	//
	// ATENÇÃO: deps = [chatAbertoCom?.id] (não vazio, não só fecharPopup).
	// Mesmo bug da página InternalChat: widget monta FECHADO (chatAbertoCom = null),
	// scroll de mensagens não renderiza, ref = null → early exit sem instalar listeners
	// NUNCA MAIS enquanto o widget ficasse montado. Agora, SEMPRE que chatAbertoCom mudar
	// (abrir nova conversa), reinstala os listeners.
	useEffect(() => {
		let cancelled = false;
		let observer = null;
		let timers = [];

		const achaLiWrapper = (el) => el instanceof Element && el.closest?.("li[data-msg-id]");
		const achaMessageId = (li) => Number(li.getAttribute("data-msg-id")) || null;
		const achaElementoBalao = (li) => li.querySelector(".rounded-\\[7\\.5px\\]");
		const dentroPortal = (el) =>
			!!(popupHostRef.current && popupHostRef.current.contains?.(el));
		const eInterativo = (el) =>
			el instanceof Element &&
			el.closest(
				"a,button,input,textarea,img,audio,video,[data-skip-react],em-emoji"
			);

		const onNativeClick = (ev) => {
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (eInterativo(t) || dentroPortal(t)) return;
			const li = achaLiWrapper(t);
			if (!li) return;
			const msgId = achaMessageId(li);
			if (msgId == null) return;
			const agora = Date.now();
			const st = msgClickState.current;
			const dt = agora - (st.lastTs || 0);
			if (st.lastId === msgId && dt < 280) {
				st.lastTs = 0;
				st.lastId = null;
				const msg = messagesRef.current.find((m) => +m.id === +msgId);
				if (msg) {
					replyToMessageRef.current?.(msg);
				}
				return;
			}
			st.lastTs = agora;
			st.lastId = msgId;
		};

		const onNativeContextMenu = (ev) => {
			ev.preventDefault();
			try { ev.stopImmediatePropagation(); } catch {}
			try { ev.stopPropagation(); } catch {}
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (dentroPortal(t)) return;
			const li = achaLiWrapper(t);
			if (!li) return;
			const msgId = achaMessageId(li);
			if (msgId == null) return;
			if (eInterativo(t)) return;
			setReactionPopoverFor((atual) =>
				+atual === +msgId ? null : msgId
			);
		};

		const onNativeDblClick = (ev) => {
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (eInterativo(t) || dentroPortal(t)) return;
			const li = achaLiWrapper(t);
			if (!li) return;
			const msgId = achaMessageId(li);
			if (msgId == null) return;
			const msg = messagesRef.current.find((m) => +m.id === +msgId);
			if (msg) {
				replyToMessageRef.current?.(msg);
			}
			msgClickState.current = { lastTs: 0, lastId: null };
		};

		const onKeydownEsc = (ev) => {
			if (ev.key === "Escape") {
				if (reactionPopoverForRef.current != null) {
					fecharPopupReacoes();
				}
			}
		};

		let rootUsado = null;

		const instalaListeners = (root) => {
			if (cancelled || !root || rootUsado === root) return;
			rootUsado = root;

			listenerNativeInstaladoRef.current = true;

			root.addEventListener("click", onNativeClick, { passive: true });
			root.addEventListener("contextmenu", onNativeContextMenu, { passive: false });
			root.addEventListener("dblclick", onNativeDblClick, { passive: true });
			document.addEventListener("keydown", onKeydownEsc, true);
		};

		const esperaInstala = () => {
			if (cancelled) return;
			const r = mensagensScrollRef.current;
			if (r) { instalaListeners(r); return; }
			let tentativas = 0;
			const tenta = () => {
				if (cancelled) return;
				tentativas++;
				const rt = mensagensScrollRef.current;
				if (rt) { instalaListeners(rt); return; }
				if (tentativas < 5) {
					const id = setTimeout(tenta, 50);
					timers.push(id);
					return;
				}
				try {
					observer = new MutationObserver(() => {
						if (cancelled && observer) { observer.disconnect(); return; }
						const rr = mensagensScrollRef.current;
						if (rr && observer) { observer.disconnect(); instalaListeners(rr); }
					});
					observer.observe(document.body, { childList: true, subtree: true });
				} catch (e) { /* noop */ }
			};
			tenta();
		};
		esperaInstala();

		return () => {
			cancelled = true;
			timers.forEach(id => clearTimeout(id));
			timers = [];
			if (observer) { try { observer.disconnect(); } catch (e) {} observer = null; }
			const rf = rootUsado || mensagensScrollRef.current;
			if (rf) {
				rf.removeEventListener("click", onNativeClick);
				rf.removeEventListener("contextmenu", onNativeContextMenu);
				rf.removeEventListener("dblclick", onNativeDblClick);
			}
			document.removeEventListener("keydown", onKeydownEsc, true);
			listenerNativeInstaladoRef.current = false;
			rootUsado = null;
		};
	}, [chatAbertoCom?.id, fecharPopupReacoes]);

	const previewMensagemCitadaW = (m) => String(m?.body || m?.mediaUrl || "").slice(0, 120) || "(anexo)";
	const nomeUsuarioNaMsgW = (m) => String(m?.fromUser?.name || m?.fromUser || "").trim() || "Usuário";

	const EMOJIS_RAPIDOS_W = [
		{ emoji: "👍", rotulo: "Joinha" },
		{ emoji: "❤️", rotulo: "Coração" },
		{ emoji: "😂", rotulo: "Risada" },
		{ emoji: "😮", rotulo: "Surpresa" },
		{ emoji: "😢", rotulo: "Tristeza" },
		{ emoji: "🙏", rotulo: "Obrigado" },
		{ emoji: "🔥", rotulo: "Fogo" },
		{ emoji: "🎉", rotulo: "Festa" },
	];


	useEffect(() => {
		userRef.current = user;
	}, [user]);
	useEffect(() => {
		chatAbertoComRef.current = chatAbertoCom;
	}, [chatAbertoCom]);
	useEffect(() => {
		chatEstadoRef.current = chatEstado;
	}, [chatEstado]);

	// (C) Scroll CONFIÁVEL para o final do container interno (overflow-y-auto).
	//
	// Problemas conhecidos que o scroll "simples" não resolve:
	//  - React aplica setMensagens → paint de forma assíncrona.
	//  - Imagens carregam lazy mesmo depois do paint (aumentando scrollHeight).
	//  - A barra de busca acima do container aparece some e altera alturas.
	//
	// Solução: múltiplas tentativas com backoff em micro + macrotasks.
	const scrollParaFim = useCallback(
		({ suave = false } = {}) => {
			const el = mensagensScrollRef.current;
			if (!el) return;
			const ir = () => {
				try {
					el.scrollTop = el.scrollHeight + 99999;
				} catch {
					/* no-op */
				}
			};
			// Microtask: pós synchronous work mas antes do paint.
			queueMicrotask(ir);
			// Primeiro rAF: antes do paint.
			requestAnimationFrame(() => {
				ir();
				// Segundo rAF encadeado: DEPOIS do primeiro paint.
				requestAnimationFrame(ir);
			});
			// Macrotasks com backoff: após paint, após lazy-load imagens.
			const delays = suave
				? [20, 50, 100, 180, 320]
				: [10, 40, 90, 160];
			delays.forEach((d) => setTimeout(ir, d));
		},
		[]
	);

	// (E) Helper: rola o container INTERNO para centralizar a mensagem com data-msg-id.
	// NÃO usar scrollIntoView — ele rola o viewport e ignora o overflow interno.
	const scrollParaMsgPorId = useCallback((msgId) => {
		const container = mensagensScrollRef.current;
		if (!container || msgId == null) return;
		const rolar = () => {
			try {
				const li = container.querySelector(`[data-msg-id="${msgId}"]`);
				if (!li) return;
				const rectContainer = container.getBoundingClientRect();
				const rectLi = li.getBoundingClientRect();
				const relTop = rectLi.top - rectContainer.top;
				const desejado =
					container.scrollTop + relTop - (container.clientHeight / 2) + (rectLi.height / 2);
				container.scrollTo({
					top: Math.max(0, desejado),
					behavior: "smooth",
				});
			} catch {
				/* no-op */
			}
		};
		queueMicrotask(rolar);
		requestAnimationFrame(() => {
			rolar();
			requestAnimationFrame(rolar);
		});
		setTimeout(rolar, 60);
	}, []);

	// (E) Calcular matches sempre que mensagens / buscaMsg mudar.
	const matches = useMemo(() => {
		const q = String(buscaMsg || "").trim();
		if (!q) return [];
		const re = new RegExp(escapeRegex(q), "i");
		const result = [];
		mensagens.forEach((m, msgIdx) => {
			const campos = [
				String(m.body || ""),
				String(m.mediaUrl || ""),
				decodeURIComponent(String(m.mediaUrl || "").split("/").pop() || ""),
			];
			if (campos.some((c) => re.test(c))) {
				result.push({ msgIdx, id: m.id });
			}
		});
		return result;
	}, [mensagens, buscaMsg]);

	// Auto-scroll para o PRIMEIRO match quando a busca tem resultado.
	useEffect(() => {
		if (matches.length > 0 && buscaAberta) {
			scrollParaMsgPorId(matches[0].id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buscaMsg, buscaAberta]);

	// Auto-scroll sempre que mensagens mudarem (recebi/enviei/peguei histórico)
	useEffect(() => {
		if (!chatAbertoCom) return;
		if (chatEstado === "minimizado") return;
		scrollParaFim();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mensagens.length, chatAbertoCom?.id, scrollParaFim]);

	const carregarEquipe = async () => {
		setCarregandoEquipe(true);
		try {
			const { data } = await api.get("/internal-chat/users-equipe");
			const lista = Array.isArray(data?.usuarios)
				? data.usuarios
				: Array.isArray(data)
				? data
				: [];
			setEquipe(lista);
		} catch {
			setEquipe([]);
		} finally {
			setCarregandoEquipe(false);
		}
	};

	// ── Listagem de conversas com dedup + AbortController (único lugar para
	//    todos os callers: mount useEffect + socket conversationUpdate/read/group).
	//    NUNCA chamar api.get("/internal-chat/conversations") direto fora daqui.
	const buscarConversasEDedup = useCallback(async () => {
		try {
			// (A) AbortController singleton: nova requisição aborta a anterior,
			// then da anterior cai em catch e NÃO grava estado obsoleto.
			if (fetchConversasAbortRef.current) {
				try { fetchConversasAbortRef.current.abort(); } catch { /* no-op */ }
				fetchConversasAbortRef.current = null;
			}
			const controller = new AbortController();
			fetchConversasAbortRef.current = controller;

			const { data } = await api.get("/internal-chat/conversations", {
				signal: controller.signal,
				timeout: 15000
			});
			// (B) Map dedup conversationKey. Mesmo se backend devolver 2 linhas
			// para a mesma conversa (join com attachments), processamos 1 só.
			const raw = Array.isArray(data) ? data : data?.conversations || [];
			const dedup = new Map();
			for (const c of raw) {
				const k = conversationKeyW(c);
				if (!k || dedup.has(k)) continue;
				dedup.set(k, c);
			}
			const convs = Array.from(dedup.values());

			// ── Mesmo processamento antigo, mas com convs DEDUPed.
			const soma = convs.reduce(
				(acc, c) => acc + Math.max(0, Number(c.unreadCount) || 0),
				0
			);
			setTotalUnread(soma);

			// Atualizar unreadCount do chat aberto (se houver):
			const aberto = chatAbertoComRef.current;
			if (aberto && convs.length) {
				const tipo = String(aberto.type || "");
				const id = Number(aberto.id || 0);
				if (id > 0) {
					const match = convs.find(
						(c) =>
							(tipo === "group" && Number(c.groupId) === id) ||
							(tipo === "direct" && Number(c.userId) === id)
					);
					if (match) {
						setChatAbertoCom((prev) =>
							prev && prev.id === aberto.id
								? { ...prev, unreadCount: Number(match.unreadCount) || 0 }
								: prev
						);
					}
				}
			}
		} catch (err) {
			// Se foi um abort da nossa parte, SILENCIOSAMENTE ignorar — é
			// esperado, outra requisição mais nova está em curso.
			const ehAbort = Boolean(err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.signal === true || (err && typeof err === "object" && String(err.message || "").toLowerCase().includes("abort")));
			if (!ehAbort) {
				// Demais erros: não crashar o widget, só não atualiza badge.
			}
		}
	}, []);

	// Carregar equipe e abrir socket
	useEffect(() => {
		if (!user?.id) return;
		// eslint-disable-next-line react-hooks/set-state-in-effect
		carregarEquipe();
		// Dedup + AbortController (buscarConversasEDedup) em vez de api.get cru
		// — evita flutuação de unreadCount no mount inicial com StrictMode 2x.
		buscarConversasEDedup();

		const socket = openSocket();
		socketRef.current = socket;

		const onMsg = ({ message }) => {
			if (!message) return;
			const eu = userRef.current;
			const fromMe = Number(message.fromUserId) === Number(eu?.id);
			const outroUserId = fromMe
				? Number(message.toUserId)
				: Number(message.fromUserId);

			// 1) SEMPRE guardar no buffer para não perder quando abrir a conversa depois
			if (!fromMe && outroUserId) {
				const buf = bufferMsgsPorUsuarioRef.current;
				const lista = buf[outroUserId] || [];
				if (!lista.find((m) =>
					(message.id && m.id === message.id) ||
					(message.__tempId && m.__tempId === message.__tempId)
				)) {
					buf[outroUserId] = [...lista, message];
				}
			}

			// 2) Atualizar lista do chat atualmente aberto (se for a mesma conversa)
			// Leitura SÍNCRONA via refs (padrão correto React 18) — evita
			// anti-padrão de setar variável fora de setState updater.
			const atualAberto = chatAbertoComRef.current;
			const ehEssaConversa = !!atualAberto && (
				(Number(message.fromUserId) === Number(atualAberto.id) &&
					Number(message.toUserId) === Number(eu?.id)) ||
				(Number(message.toUserId) === Number(atualAberto.id) && fromMe)
			);

			if (ehEssaConversa) {
				setMensagens((m) => {
					if (m.find((x) =>
						(message.id && x.id === message.id) ||
						(message.__tempId && x.__tempId === message.__tempId)
					)) return m;
					return [...m, message];
				});
				// Limpar o buffer desta conversa pois já inserimos no estado aberto
				if (outroUserId && !fromMe) {
					const buf = bufferMsgsPorUsuarioRef.current;
					if (buf[outroUserId]) delete buf[outroUserId];
				}
			}

			// 3) Notificação piscante + badge (se NÃO fui eu e popup está minimizado)
			// Leitura SÍNCRONA de chatEstado via ref também.
			if (!fromMe && ehEssaConversa && chatEstadoRef.current === "minimizado") {
				setPiscandoMinimizado(true);
				setNovasCount((c) => c + 1);
			}

			// 4) Badge global e som (sempre)
			if (!fromMe) {
				setTotalUnread((t) => t + 1);
				tocarSomNovoChatInterno();
				// ================================================================
				// ITEM 6: destacar pessoa que ENVIOU a mensagem no TOPO da lista
				// popup "Chat interno da equipe" (ao abrir, o usuário que acabou
				// de me mandar mensagem aparece primeiro, igual WhatsApp).
				//
				// Regra: só aplicamos se a mensagem NAO veio de mim (fromMe=false)
				// e temos o outro userId válido.
				// setEquipe updater com previous state: filter out -> unshift no top
				// (se ele já estava no topo, nenhum efeito visual colateral).
				// ================================================================
				if (outroUserId) {
					setEquipe((anterior) => {
						try {
							const idDesejado = Number(outroUserId);
							if (!Number.isFinite(idDesejado) || !Array.isArray(anterior)) return anterior;
							const achado = anterior.find((x) => Number(x.id) === idDesejado);
							if (!achado) return anterior;
							const restante = anterior.filter((x) => Number(x.id) !== idDesejado);
							// Evita re-render desnecessário se já estava na posição 0
							if (restante.length === anterior.length - 1 && anterior[0] === achado) return anterior;
							return [achado, ...restante];
						} catch {
							return anterior;
						}
					});
				}
				try {
					if ("Notification" in window && Notification.permission === "granted") {
						// NOTIFICATION API SILENT FLAG — evita o som PADRÃO do
						// SISTEMA OPERACIONAL (Chrome/Windows/macOS) que NÃO
						// passa pelo Howler nem chatInternoSom. Ler o valor
						// NA HORA do módulo singleton.
						const vRawIC = Number(getChatInternoNotifVolume01());
						const vPlayIC = Number.isFinite(vRawIC)
							? Math.max(0, Math.min(1, vRawIC))
							: 0;
						const ehMudoIC = vPlayIC <= 0.01;
						new Notification(
							`${message.fromUser?.name || "Chat interno"} — equipe`,
							{
								body:
									String(message.body || message.mediaUrl || "Arquivo").slice(0, 120),
								icon: message.fromUser?.profilePicUrl
									? getPublicFileUrl(message.fromUser.profilePicUrl)
									: undefined,
								tag: `chatInterno-${message.fromUserId}`,
								silent: ehMudoIC,
							}
						);
					}
				} catch {
					/* no-op */
				}
			}
		};

		const onPresenceChange = ({ userId, status }) => {
			setEquipe((anterior) =>
				anterior.map((u) =>
					Number(u.id) === Number(userId) ? { ...u, status } : u
				)
			);
			setChatAbertoCom((anterior) =>
				anterior && Number(anterior.id) === Number(userId)
					? { ...anterior, status }
					: anterior
			);
		};

		const onReactionW = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			try { dbgPushW({ ev: "widget_socket_reaction_recv", mid: String(payload.messageId), t: Date.now() }); } catch { /* no-op */ }
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return { ...m, reacoes: Array.isArray(payload.reacoes) ? payload.reacoes : [] };
				});
				return mudou ? novas : prev;
			});
		};

		const onFavoriteW = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return { ...m, favorita: !!payload.favorita };
				});
				return mudou ? novas : prev;
			});
		};

		const onMessageEditW = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return {
						...m,
						body: typeof payload.body === "string" ? payload.body : m.body,
						editedAt: payload.editedAt || m.editedAt || new Date().toISOString(),
						transcription:
							typeof payload.transcription === "string"
								? payload.transcription
								: m.transcription,
						transcribedAt: payload.transcribedAt || m.transcribedAt || null,
					};
				});
				return mudou ? novas : prev;
			});
		};

		// ── Conversation update: qualquer fluxo que tocou unread ou última
		// mensagem (envio paralelo, markAsRead em outra aba, grupo, etc.)
		// Recarrega a lista de conversas rapidinho para atualizar:
		//   - TotalUnread do topo (badge do ícone do chat widget)
		//   - unreadCount da conversa ABERTA agora (se for o caso)
		// Sem isto: o widget mostrava contagem velha até F5.
		//
		// Chamada DEDUP + ABORT (buscarConversasEDedup). Antes: 2x socket events
		// no mesmo ms → 2 requests → race condition → resposta velha pisa nova.
		const onConversationUpdateW = () => {
			buscarConversasEDedup();
		};

		socket.on("internalChat:message", onMsg);
		socket.on("userPresenceChange", onPresenceChange);
		socket.on("internalChat:reaction", onReactionW);
		socket.on("internalChat:favorite", onFavoriteW);
		socket.on("internalChat:messageEdit", onMessageEditW);
		socket.on("internalChat:conversationUpdate", onConversationUpdateW);
		socket.on("internalChat:read", onConversationUpdateW);
		socket.on("internalChat:group", onConversationUpdateW);

		// Pedir permissão de notificação na primeira
		try {
			if ("Notification" in window && Notification.permission === "default") {
				Notification.requestPermission().catch(() => undefined);
			}
		} catch {
			/* no-op */
		}

		const intervalo = setInterval(carregarEquipe, 60000);
		return () => {
			try { socket.off("internalChat:message", onMsg); } catch (_) {}
			try { socket.off("userPresenceChange", onPresenceChange); } catch (_) {}
			try { socket.off("internalChat:reaction", onReactionW); } catch (_) {}
			try { socket.off("internalChat:favorite", onFavoriteW); } catch (_) {}
			try { socket.off("internalChat:messageEdit", onMessageEditW); } catch (_) {}
			try { socket.off("internalChat:conversationUpdate", onConversationUpdateW); } catch (_) {}
			try { socket.off("internalChat:read", onConversationUpdateW); } catch (_) {}
			try { socket.off("internalChat:group", onConversationUpdateW); } catch (_) {}
			// SOCKET DISCONNECT NÃO PODE MAIS aqui: é singleton compartilhado
			// com a fila de tickets, transferências, visto, presença, chat interno etc.
			// Disconnect aqui matava a conexão de TODO o resto do sistema → todos
			// updates paravam até o próximo F5.
			socketRef.current = null;
			clearInterval(intervalo);
		};
	}, [user?.id]);

	// Scrollar para baixo ao adicionar mensagens
	useEffect(() => {
		if (!chatAbertoCom) return;
		if (chatEstado === "minimizado") return;
		scrollParaFim({ suave: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mensagens.length, chatAbertoCom?.id, chatEstado]);

	// ===== CLIQUE FORA = MINIMIZA (não fecha!) =====
	useEffect(() => {
		if (!chatAbertoCom || chatEstado !== "aberto") return;
		const handler = (e) => {
			const el = chatContainerRef.current;
			const trigger = document.querySelector("[data-chat-interno-trigger]");
			const popover = document.querySelector("[data-radix-popper-content-wrapper]");
			// #region debug-point widget_clique_fora_1
			const pathWidget = typeof e.composedPath === "function" ? e.composedPath() : [];
			const pathTemPopupReacao = pathWidget.some(function (n) {
				if (!(n instanceof Element)) return false;
				return n.matches(
					"[data-popup-host-internal-chat-widget], [data-popup-host-internal], [data-reaction-popover], [data-skip-react], [data-figurinha-picker-portal]"
				);
			});
			dbgPushW({
				ev: "widget_clique_fora_check",
				ts: Date.now(),
				pathLen: pathWidget.length,
				contemEl: !!(el && el.contains(e.target)),
				contemTrigger: !!(trigger && trigger.contains(e.target)),
				contemPopper: !!(popover && popover.contains(e.target)),
				contemPopupReacao: pathTemPopupReacao,
				evtPhase: e.eventPhase,
				targetCls: e.target instanceof Element ? (e.target.className||'').slice(0,60) : String(e.target),
				botao: String(e.button),
			});
			// #endregion
			if (el && el.contains(e.target)) return;
			if (trigger && trigger.contains(e.target)) return;
			if (popover && popover.contains(e.target)) return;
			if (pathTemPopupReacao) return; // <-- guarda parcial para debug (nao minimiza reacao!)
			dbgPushW({ ev: "widget_clique_fora_MINIMIZA_disparou", ts: Date.now() });
			setChatEstado("minimizado");
		};
		// microdelay para não atuar no mesmo clique que abriu
		const t = setTimeout(() => {
			document.addEventListener("mousedown", handler, true);
		}, 0);
		return () => {
			clearTimeout(t);
			document.removeEventListener("mousedown", handler, true);
		};
	}, [chatAbertoCom, chatEstado]);

	const equipeFiltrada = useMemo(() => {
		const q = busca.trim().toLowerCase();
		if (!q) return equipe;
		return equipe.filter((u) =>
			String(u.name || "")
				.toLowerCase()
				.includes(q)
		);
	}, [equipe, busca]);

	const abrirChatCom = (usuario) => {
		if (!usuario) return;
		setPopoverAberto(false);
		setChatAbertoCom(usuario);
		setChatEstado("aberto");
		setMensagens([]);
		setTexto("");
		setAnexos([]);
		setPiscandoMinimizado(false);
		setNovasCount(0);
		setBuscaMsg("");
		setIndiceMatch(0);
		(async () => {
			setCarregandoMsgs(true);
			try {
				const { data } = await api.get(
					`/internal-chat/direct/${usuario.id}/messages`,
					{ params: { pageSize: 40 } }
				);
				let msgs = Array.isArray(data?.messages) ? data.messages : [];
				// Aplicar buffer recebido em tempo real enquanto conversa estava fechada
				const buf = bufferMsgsPorUsuarioRef.current;
				const msgsBuffer = buf[usuario.id] || [];
				if (msgsBuffer.length) {
					delete buf[usuario.id];
					const juntas = [...msgs, ...msgsBuffer];
					const vistas = new Set();
					msgs = [];
					for (const m of juntas) {
						const k = m.id ? `id-${m.id}` : `tmp-${m.__tempId || Math.random()}`;
						if (!vistas.has(k)) {
							vistas.add(k);
							msgs.push(m);
						}
					}
					msgs.sort((a, b) => {
						const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
						const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
						return da - db;
					});
				}
				setMensagens(msgs);
				// Marcar como lido
				api
					.post(`/internal-chat/${usuario.id}/read?type=direct`)
					.catch(() => undefined);
				// Diminuir unread (total)
				setTotalUnread((t) =>
					Math.max(
						0,
						t - msgs.filter((m) => Number(m.fromUserId) !== Number(user?.id)).length
					)
				);
			} finally {
				setCarregandoMsgs(false);
				requestAnimationFrame(() =>
					inputRef.current?.focus?.()
				);
				scrollParaFim({ suave: true });
			}
		})();
	};

	const minimizarChat = () => setChatEstado("minimizado");

	const maximizarChat = () => {
		setChatEstado("aberto");
		setPiscandoMinimizado(false);
		setNovasCount(0);
		requestAnimationFrame(() => {
			inputRef.current?.focus?.();
			scrollParaFim({ suave: true });
		});
	};

	const toggleBusca = () => {
		if (chatEstado !== "aberto") return;
		setBuscaAberta((antes) => {
			const abrir = !antes;
			if (abrir) setTimeout(() => buscaMsgInputRef.current?.focus?.(), 30);
			else setBuscaMsg("");
			return abrir;
		});
	};

	const navegarMatch = (delta) => {
		if (!matches.length) return;
		setIndiceMatch((anterior) => {
			const prox = (anterior + delta + matches.length) % matches.length;
			const alvo = matches[prox];
			if (alvo) scrollParaMsgPorId(alvo.id);
			return prox;
		});
	};

	const fecharChat = () => {
		setChatAbertoCom(null);
		setChatEstado("aberto");
		setMensagens([]);
		setAnexos([]);
		setTexto("");
	};

	const adicionarAnexos = (arquivos) => {
		const arr = Array.from(arquivos || []);
		if (!arr.length) return;
		setAnexos((anteriores) => [
			...anteriores,
			...arr.map((arq) => {
				const ehImagem = arq.type?.startsWith("image/") ||
					/^image/i.test(tipoDeMidia(arq.type || arq.name));
				const preview = ehImagem ? URL.createObjectURL(arq) : null;
				return { file: arq, preview };
			}),
		]);
	};

	const removerAnexo = (index) => {
		setAnexos((antes) => {
			const prox = [...antes];
			const [removido] = prox.splice(index, 1);
			if (removido?.preview)
				try { URL.revokeObjectURL(removido.preview); } catch { /* no-op */ }
			return prox;
		});
	};

	const handlePaste = (e) => {
		if (!e.clipboardData) return;
		const cboard = e.clipboardData;

		let arquivosColados = [];
		try {
			arquivosColados = Array.from(cboard.files || []);
		} catch (err) {
			arquivosColados = [];
		}

		if (arquivosColados.length === 0 && cboard.items) {
			const arr = [];
			for (let i = 0; i < cboard.items.length; i++) {
				const it = cboard.items[i];
				if (it && it.kind === "file") {
					const arq = it.getAsFile && it.getAsFile();
					if (arq) arr.push(arq);
				}
			}
			arquivosColados = arr;
		}
		if (!arquivosColados.length) return;

		e.preventDefault();
		adicionarAnexos(arquivosColados);
	};

	const handleStartRecordW = async () => {
		if (gravando) return;
		const formato = escolherFormatoAudioW();
		if (!formato) {
			toast.error("Este navegador não suporta gravação de áudio.");
			return;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const gravador = formato.mime
				? new MediaRecorder(stream, { mimeType: formato.mime })
				: new MediaRecorder(stream);
			pedacosRef.current = [];
			gravadorRef.current = { recorder: gravador, stream, ext: formato.ext };
			gravador.ondataavailable = (evento) => {
				if (evento.data && evento.data.size > 0) pedacosRef.current.push(evento.data);
			};
			gravador.start(1000);
			setGravando(true);
		} catch (err) {
			if (err?.name === "NotAllowedError") {
				toast.error("Você bloqueou o microfone. Libere no cadeado ao lado do endereço.");
			} else if (err?.name === "NotFoundError") {
				toast.error("Nenhum microfone encontrado.");
			} else if (toastError) {
				toastError(err);
			} else {
				console.error(err);
			}
		}
	};

	const encerrarGravacaoW = () =>
		new Promise((resolve) => {
			const atual = gravadorRef.current;
			if (!atual) { resolve(null); return; }
			const finalizar = () => {
				atual.stream.getTracks().forEach((t) => t.stop());
				gravadorRef.current = null;
				const blob = new Blob(pedacosRef.current, {
					type: atual.recorder.mimeType || "audio/webm",
				});
				pedacosRef.current = [];
				resolve({ blob, ext: atual.ext });
			};
			if (atual.recorder.state === "inactive") { finalizar(); return; }
			atual.recorder.onstop = finalizar;
			atual.recorder.stop();
		});

	const handleUploadAudioW = async () => {
		try {
			const gravado = await encerrarGravacaoW();
			setGravando(false);
			if (!gravado) return;
			if (gravado.blob.size < 2000) {
				toast.info("Gravação muito curta. Segure o botão enquanto fala.");
				return;
			}
			const wav = await paraWav(gravado.blob);
			const nome = `${new Date().getTime()}.wav`;
			const arquivo = new File([wav], nome, { type: "audio/wav" });
			adicionarAnexos([arquivo]);
			await new Promise((res) => setTimeout(res, 120));
			enviarMensagem();
		} catch (err) {
			console.error(err);
			if (toastError) toastError(err);
			setGravando(false);
		}
	};

	const handleCancelAudioW = async () => {
		await encerrarGravacaoW();
		setGravando(false);
	};

	const handleDrop = (e) => {
		if (!e.dataTransfer) return;
		e.preventDefault();
		setDragOver(false);
		const files = Array.from(e.dataTransfer.files || []);
		adicionarAnexos(files);
	};

	const enviarMensagem = async () => {
		if (!chatAbertoCom) return;
		const corpo = texto.trim();
		if (!corpo && anexos.length === 0) return;
		setEnviando(true);
		try {
			const form = new FormData();
			if (corpo) form.append("body", corpo);
			if (quotedMessage?.id) form.append("quotedMsgId", String(quotedMessage.id));
			anexos.forEach((a) => form.append("medias", a.file, a.file.name));

			// Anexa optimistic
			const tempId = `tmp_${Date.now()}`;
			const temporaria = {
				__tempId: tempId,
				id: -Math.floor(Math.random() * 1e9),
				body: corpo || null,
				mediaUrl: anexos[0]?.file?.name || null,
				mediaType: anexos[0]?.file?.type || null,
				fromUserId: Number(user?.id),
				toUserId: Number(chatAbertoCom.id),
				createdAt: new Date().toISOString(),
				fromUser: {
					id: Number(user?.id),
					name: user?.name || "Eu",
					profilePicUrl: user?.profilePicUrl || null,
				},
				quotedMsg: quotedMessage && quotedMessage.id ? {
					id: quotedMessage.id,
					fromUserId: quotedMessage.fromUserId,
					fromUser: quotedMessage.fromUser,
					body: quotedMessage.body,
					mediaUrl: quotedMessage.mediaUrl,
					mediaType: quotedMessage.mediaType,
				} : null,
			};
			setMensagens((m) => [...m, temporaria]);
			msgsEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
			tocarSomEnviadoChatInterno();

			const { data } = await api.post(
				`/internal-chat/direct/${chatAbertoCom.id}/messages`,
				form,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);
			const enviadas = Array.isArray(data) ? data : [data];
			setMensagens((m) => {
				const semTemp = m.filter((x) => x.__tempId !== tempId);
				return [...semTemp, ...enviadas];
			});
			setTexto("");
			setQuotedMessage(null);
			setAnexos((lista) => {
				lista.forEach((a) => {
					if (a.preview)
						try { URL.revokeObjectURL(a.preview); } catch { /* no-op */ }
				});
				return [];
			});
		} catch {
			/* silent */
		} finally {
			setEnviando(false);
			requestAnimationFrame(() => inputRef.current?.focus?.());
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			enviarMensagem();
		}
	};

	// ============================================================
	// RENDER
	// ============================================================
	return (
		<>
			<style>{`
				@keyframes piscaWidget {
					0%, 100% {
						box-shadow: 0 0 0 0 rgba(0,168,132,0), 0 20px 60px -15px rgba(0,0,0,0.45), 0 10px 20px -8px rgba(0,0,0,0.25);
						border-color: var(--cor-borda-pisca-normal, inherit);
					}
					50% {
						box-shadow: 0 0 0 3px rgba(0,168,132,0.7), 0 0 40px 8px rgba(0,168,132,0.55), 0 20px 60px -15px rgba(0,0,0,0.45), 0 10px 20px -8px rgba(0,0,0,0.25);
						border-color: #00a884;
					}
				}
				.internal-chat-piscar {
					animation: piscaWidget 1.1s ease-in-out infinite !important;
					border-style: solid !important;
				}
			`}</style>
			<Popover open={popoverAberto} onOpenChange={setPopoverAberto}>
				<PopoverTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="relative"
						aria-label="Chat interno da equipe"
						title="Chat interno da equipe"
						data-chat-interno-trigger="true"
						onClick={() => {
							if (popoverAberto) {
								setPopoverAberto(false);
							} else {
								setTotalUnread(0);
								carregarEquipe();
								setPopoverAberto(true);
							}
						}}
					>
						<MessageCircle className="h-5 w-5" />
						{totalUnread > 0 && (
							<span
								className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow-sm"
								style={{ backgroundColor: tema.badgeUnread }}
							>
								{totalUnread > 99 ? "99+" : totalUnread}
							</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					className="p-0"
					style={{
						width: 330,
						backgroundColor: tema.popoverBg,
						borderColor: tema.popoverBorder,
						color: tema.corTextoRecebida,
					}}
				>
					<div
						className="flex items-center gap-2 border-b px-3 py-2"
						style={{
							borderColor: tema.borderSeparator,
							backgroundColor: tema.bgHeader,
						}}
					>
						<MessageCircle
							className="h-4 w-4 shrink-0"
							style={{ color: tema.verdeWpp }}
						/>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-semibold">
								Chat interno da equipe
							</p>
							<p
								className="truncate text-[11px]"
								style={{ color: tema.textoMuted }}
							>
								{equipe.filter((u) => u.status === "online").length} on-line
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 shrink-0"
							onClick={() => setPopoverAberto(false)}
							style={{ color: tema.textoMuted }}
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
					<div className="px-2 pt-2">
						<div className="relative">
							<Search
								className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
								style={{ color: tema.textoMuted }}
							/>
							<Input
								value={busca}
								onChange={(e) => setBusca(e.target.value)}
								placeholder="Buscar pessoa..."
								className="pl-8 h-8 text-sm"
								style={{
									backgroundColor: tema.bgInput,
									borderColor: tema.borderSeparator,
									color: tema.corTextoRecebida,
								}}
							/>
						</div>
					</div>
					<div className="max-h-[360px] mt-1 overflow-y-auto">
						{carregandoEquipe && (
							<div
								className="p-4 text-center text-xs"
								style={{ color: tema.textoMuted }}
							>
								carregando...
							</div>
						)}
						{!carregandoEquipe && equipeFiltrada.length === 0 && (
							<div
								className="p-4 text-center text-xs"
								style={{ color: tema.textoMuted }}
							>
								Ninguém da equipe por aqui.
							</div>
						)}
						<ul className="flex flex-col p-1">
							{equipeFiltrada.map((u) => (
								<li key={u.id}>
									<button
										type="button"
										onClick={() => abrirChatCom(u)}
										className="group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors"
										style={{
											color: tema.corTextoRecebida,
											backgroundColor:
												"transparent",
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.backgroundColor = tema.bgSidebarHover;
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = "transparent";
										}}
									>
										<div className="relative">
											<AvatarUsuario
												name={u.name}
												profilePicUrl={u.profilePicUrl}
												size="md"
											/>
											<span
												className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
												style={{
													backgroundColor: corPontoPresenca(u.status),
													borderColor: tema.popoverBg,
												}}
											/>
										</div>
										<div className="min-w-0 flex-1">
											<p className="truncate text-sm font-medium">
												{u.name}
											</p>
											<p
												className="truncate text-[11px]"
												style={{ color: tema.textoMuted }}
											>
												{rotuloPresenca(u.status)}
											</p>
										</div>
									</button>
								</li>
							))}
						</ul>
					</div>
				</PopoverContent>
			</Popover>

			{/* =========================================================
			    CHAT FLUTUANTE — INFERIOR DIREITA (não bloqueia tela!)
			   ========================================================= */}
			{chatAbertoCom && (
				<div
					className={cn(
						"fixed right-5 z-[9999] select-none",
						chatEstado === "minimizado" ? "bottom-5" : "bottom-5"
					)}
					style={{}}
					onContextMenu={(e) => {
						e.preventDefault();
						try { e.stopImmediatePropagation(); } catch {}
						try { e.stopPropagation(); } catch {}
					}}
				>
					<div
						ref={chatContainerRef}
						data-chat-interno-janela="true"
						className={cn(
							"relative rounded-xl overflow-hidden border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.45),0_10px_20px_-8px_rgba(0,0,0,0.25)] flex flex-col",
							piscandoMinimizado && "internal-chat-piscar"
						)}
						style={{
							width: chatEstado === "minimizado" ? 290 : CHAT_W,
							height: chatEstado === "minimizado" ? "auto" : CHAT_H,
							maxWidth: "min(94vw, 520px)",
							maxHeight: chatEstado === "minimizado" ? "none" : "min(88vh, 720px)",
							backgroundColor: tema.bgSidebar,
							borderColor: piscandoMinimizado ? "#00a884" : tema.borderSeparator,
							color: tema.corTextoRecebida,
						}}
						onContextMenu={(e) => {
							e.preventDefault();
							try { e.stopImmediatePropagation(); } catch {}
							try { e.stopPropagation(); } catch {}
						}}
					>
						{/* ===== HEADER: único, tanto minimizado quanto aberto tem cabeçalho ===== */}
						<div
							className={cn(
								"flex items-center gap-3 border-b px-3 shrink-0",
								chatEstado === "minimizado"
									? "py-2 cursor-pointer hover:brightness-[0.98] transition"
									: "py-2.5"
							)}
							style={{
								backgroundColor: tema.bgHeader,
								borderColor: tema.borderSeparator,
							}}
							onClick={chatEstado === "minimizado" ? maximizarChat : undefined}
							onContextMenu={(e) => {
								e.preventDefault();
								try { e.stopImmediatePropagation(); } catch {}
								try { e.stopPropagation(); } catch {}
							}}
						>
							<div className="relative shrink-0">
								<AvatarUsuario
									name={chatAbertoCom.name}
									profilePicUrl={chatAbertoCom.profilePicUrl}
									size={chatEstado === "minimizado" ? "md" : "lg"}
								/>
								<span
									className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
									style={{
										backgroundColor: corPontoPresenca(chatAbertoCom.status),
										borderColor: tema.bgHeader,
									}}
								/>
							</div>
							<div className="min-w-0 flex-1">
								<p className={cn("truncate font-semibold leading-tight", chatEstado === "minimizado" ? "text-[13px]" : "text-sm")}>
									{chatAbertoCom?.name}
								</p>
								<p
									className={cn("truncate leading-tight mt-0.5", chatEstado === "minimizado" ? "text-[10.5px]" : "text-[11px]")}
									style={{ color: tema.textoMuted }}
								>
									{rotuloPresenca(chatAbertoCom.status)}
								</p>
							</div>
							{/* Badge de NÃO LIDAS quando minimizado */}
							{chatEstado === "minimizado" && novasCount > 0 && (
								<span
									className="absolute top-1.5 right-16 flex h-[20px] min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow"
									style={{ backgroundColor: "#00a884" }}
									title={`${novasCount} mensagens novas`}
								>
									{novasCount > 99 ? "99+" : novasCount}
								</span>
							)}
							{/* Botões do header: busca — minimizar — e fechar X (todos param propagação) */}
							<div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
								{chatEstado === "aberto" && (
									<Button
										variant="ghost"
										size="icon"
										className={cn(
											"h-8 w-8 shrink-0",
											buscaAberta && "bg-[#00a884]/10"
										)}
										onClick={toggleBusca}
										style={{ color: buscaAberta ? "#008069" : tema.textoMuted }}
										aria-label="Buscar na conversa"
										title="Buscar na conversa"
									>
										<Search className="h-4 w-4" />
									</Button>
								)}
								{chatEstado === "aberto" && (
									<Button
										variant="ghost"
										size="icon"
										className="h-8 w-8 shrink-0"
										onClick={minimizarChat}
										style={{ color: tema.textoMuted }}
										aria-label="Minimizar chat"
										title="Minimizar"
									>
										<Minus className="h-4 w-4" />
									</Button>
								)}
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									onClick={fecharChat}
									style={{ color: tema.textoMuted }}
									aria-label="Fechar chat"
									title="Fechar"
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</div>

						{/* ===== SÓ RENDERIZA CORPO / ANEXOS / INPUT SE ESTIVER ABERTO ===== */}
						{chatEstado === "aberto" && (
							<>
								{/* (E) Barra de busca por mensagens (abaixo do header) */}
								{buscaAberta && (
									<div
										className="flex items-center gap-2 border-b px-3 py-2 shrink-0"
										style={{
											backgroundColor: tema.bgHeader,
											borderColor: tema.borderSeparator,
										}}
									>
										<Search
											className="h-3.5 w-3.5 shrink-0"
											style={{ color: tema.textoMuted }}
										/>
										<Input
											ref={buscaMsgInputRef}
											value={buscaMsg}
											onChange={(e) => {
												setBuscaMsg(e.target.value);
												setIndiceMatch(0);
											}}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													navegarMatch(e.shiftKey ? -1 : 1);
												}
											}}
											placeholder="Buscar na conversa"
											className="h-8 min-w-0 flex-1 text-sm"
											style={{
												backgroundColor: tema.inputBg,
												borderColor: tema.borderSeparator,
												color: tema.corTextoRecebida,
											}}
										/>
										<div
											className="shrink-0 text-[11px] tabular-nums min-w-[40px] text-center"
											style={{ color: tema.textoMuted }}
										>
											{matches.length === 0
												? buscaMsg.trim()
													? "0/0"
													: "—"
												: `${indiceMatch + 1}/${matches.length}`}
										</div>
										<div className="flex items-center gap-0.5 shrink-0">
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												onClick={() => navegarMatch(-1)}
												disabled={matches.length === 0}
												title="Anterior (Shift+Enter)"
												style={{ color: tema.textoMuted }}
												aria-label="Anterior"
											>
												<ChevronUp className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												onClick={() => navegarMatch(1)}
												disabled={matches.length === 0}
												title="Próximo (Enter)"
												style={{ color: tema.textoMuted }}
												aria-label="Próximo"
											>
												<ChevronDown className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7 ml-0.5"
												onClick={() => {
													setBuscaAberta(false);
													setBuscaMsg("");
													setIndiceMatch(0);
												}}
												title="Fechar busca"
												style={{ color: tema.textoMuted }}
												aria-label="Fechar busca"
											>
												<X className="h-3.5 w-3.5" />
											</Button>
										</div>
									</div>
								)}

								{/* CORPO — flex:1 + min-h-0: SEM espaço branco, independente do tamanho restante */}
								<div
									className="relative flex flex-1 min-h-0 overflow-hidden"
									style={{
										backgroundImage: tema.bgChatDots,
										backgroundColor: tema.bgChat,
									}}
									onDragOver={(e) => {
										e.preventDefault();
										setDragOver(true);
									}}
									onDragLeave={() => setDragOver(false)}
									onDrop={handleDrop}
								>
									{dragOver && (
										<div
											className="pointer-events-none absolute inset-2 rounded-xl border-2 border-dashed z-10 flex items-center justify-center backdrop-blur-sm"
											style={{
												borderColor: tema.verdeWpp,
												backgroundColor: darkMode
													? "rgba(0,168,132,0.12)"
													: "rgba(0,168,132,0.08)",
												color: tema.verdeWpp,
											}}
										>
											<p className="text-sm font-semibold">
												Soltar para anexar arquivo
											</p>
										</div>
									)}
									<div
										ref={mensagensScrollRef}
										className="h-full w-full px-3 py-3 overflow-y-auto"
									>
										{carregandoMsgs && (
											<div
												className="text-center text-xs py-8"
												style={{ color: tema.textoMuted }}
											>
												carregando mensagens...
											</div>
										)}
										{!carregandoMsgs && mensagens.length === 0 && (
											<div
												className="text-center text-xs py-10"
												style={{ color: tema.textoMuted }}
											>
												Comece a conversa. Envie uma mensagem para{" "}
												<span className="font-semibold" style={{ color: tema.verdeWppEscuro }}>
													{chatAbertoCom?.name}
												</span>
												.
											</div>
										)}
										<ul ref={mensagensListaRef} className="flex flex-col gap-1.5">
											{mensagens.map((m, i) => {
												const isMine =
													Number(m.fromUserId) === Number(user?.id) ||
													Boolean(m.__tempId);
												const diaAtual = chaveDia(m.createdAt);
												const diaAnterior =
													i === 0 ? null : chaveDia(mensagens[i - 1].createdAt);
												const mudouDia = diaAtual && diaAtual !== diaAnterior;
												const ehMatchAtual =
													buscaMsg.trim() &&
													matches[indiceMatch]?.id === m.id;
												return (
													<React.Fragment key={m.id + "-" + i}>
														{/* (D) Separador por dia igual WhatsApp */}
														{mudouDia && (
															<li
																key={`sep-${diaAtual}-${i}`}
																className="flex justify-center my-1.5"
															>
																<span
																	className="rounded-full px-2.5 py-0.5 text-[10.5px] font-medium shadow-sm"
																	style={{
																		backgroundColor: darkMode
																			? "rgba(255,255,255,0.06)"
																			: "rgba(255,255,255,0.92)",
																		color: tema.textoMuted,
																	}}
																>
																	{rotuloDia(diaAtual)}
																</span>
															</li>
														)}
														<li
															data-msg-id={m.id}
															key={`msg-${m.id}-${i}`}
															className={`flex ${isMine ? "justify-end" : "justify-start"}`}
														>
															<div
																className={cn(
																	"relative max-w-[80%] rounded-[7.5px] px-2.5 py-1.5 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] space-y-1",
																	ehMatchAtual && "ring-2 ring-[#ffcc4d]"
																)}
																ref={(el) => {
																	if (el) balaoRefsSet(m.id, { el, isMine });
																	else balaoRefsSet(m.id, null);
																}}
																style={{
																	backgroundColor: isMine
																		? tema.balaoEnviada
																		: tema.balaoRecebida,
																	color: isMine
																		? tema.corTextoEnviada
																		: tema.corTextoRecebida,
																	borderTopRightRadius: isMine ? "2px" : undefined,
																	borderTopLeftRadius: !isMine ? "2px" : undefined,
																}}
															>
																{!isMine && (
																	<p
																		className="mb-0.5 text-[12px] font-semibold leading-tight"
																		style={{ color: tema.verdeWppEscuro }}
																	>
																		{m.fromUser?.name || chatAbertoCom?.name || "Usuário"}
																	</p>
																)}
																<RenderizarMidiaMin
																	mensagem={m}
																	tema={tema}
																	darkMode={darkMode}
																	onAbrirImagem={(url, nome) =>
																		setImagemModalWidget({ url, nome })
																	}
																	abrirArquivo={abrirArquivoW}
																	transcrevendo={transcrevendoIdsW.includes(+m.id)}
																	transcription={m.transcription}
																	transcribedAt={m.transcribedAt}
																	onTranscrever={(opts) => handleTranscreverW(m, opts || {})}
																/>
																{m.body && (
																	<p
																		className="whitespace-pre-wrap break-words text-[13.5px] leading-[18px] select-text"
																		style={{
																			wordBreak: "break-word",
																			overflowWrap: "anywhere",
																			hyphens: "auto",
																		}}
																	>
																		{destacarTexto(m.body, buscaMsg, ehMatchAtual)}
																	</p>
																)}
																{Array.isArray(m.reacoes) && m.reacoes.length > 0 ? null : (
																	<div style={{ height: "0px" }} aria-hidden />
																)}
																<div className="flex items-end justify-between gap-2 mt-0.5">
																	{Array.isArray(m.reacoes) && m.reacoes.length > 0 ? (
																		<div className="flex flex-wrap gap-1 justify-start min-w-0">
																			{m.reacoes.map((r) => {
																				const total = Number(r.total) || r.userIds?.length || 1;
																				const euReagi =
																					Array.isArray(r.userIds) &&
																					r.userIds.includes(Number(user?.id));
																				return (
																					<button
																						type="button"
																						key={String(r.emoji)}
																						data-skip-react
																						onClick={(e) => {
																							e.stopPropagation();
																							toggleReaction(m.id, r.emoji);
																						}}
																						className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]`}
																						style={{
																							backgroundColor: euReagi
																								? "#e8f4ee"
																								: isMine
																									? "#f5f5f5"
																									: "#f7f7f7",
																							borderColor: "#ffffff",
																							boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
																							color: "#111b21",
																						}}
																					>
																						<span className="text-sm leading-none">
																							{r.emoji}
																						</span>
																						<span className="font-semibold">{total}</span>
																					</button>
																				);
																			})}
																		</div>
																	) : (
																		<span className="flex-1" aria-hidden />
																	)}
																	<p
																		className="pointer-events-none shrink-0 text-[10px] whitespace-nowrap ml-2"
																		style={{
																			color: isMine
																				? tema.corTimeEnviada
																				: tema.corTimeRecebida,
																		}}
																	>
																		{m.createdAt
																			? format(new Date(m.createdAt), "HH:mm")
																			: "enviando"}
																	</p>
																</div>
															</div>
														</li>
													</React.Fragment>
												);
											})}
											<div ref={msgsEndRef} />
										</ul>
									</div>
								</div>

								{/* (F1) Preview QUOTED reply inline — aparece acima dos anexos e do input */}
								{quotedMessage && (
									<div
										className="flex items-start gap-2 border-b px-3 py-2"
										style={{
											borderBottomColor: "#e9edef",
											backgroundColor: "#ffffff",
										}}
									>
										<div
											className="w-1 shrink-0 self-stretch rounded-sm"
											style={{
												backgroundColor: Number(quotedMessage.fromUserId) === Number(user?.id)
													? tema.borderQuotedMinha
													: tema.borderQuoted,
											}}
										/>
										<div className="min-w-0 flex-1">
											<p
												className="truncate text-[12px] font-semibold"
												style={{
													color: Number(quotedMessage.fromUserId) === Number(user?.id)
														? tema.borderQuotedMinha
														: tema.borderQuoted,
												}}
											>
												{nomeUsuarioNaMsgW(quotedMessage)}
											</p>
											<p
												className="mt-0.5 line-clamp-2 text-[12px]"
												style={{ color: tema.corTextoRecebida }}
											>
												{previewMensagemCitadaW(quotedMessage)}
											</p>
										</div>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 shrink-0"
											onClick={cancelReply}
											title="Cancelar resposta"
										>
											<X className="h-3.5 w-3.5" />
										</Button>
									</div>
								)}

								{/* ANEXOS PRÉVIA — SHRINK-0, não encolhe */}
								{anexos.length > 0 && (
									<div
										className="flex flex-wrap gap-2 border-t px-3 py-2 shrink-0"
										style={{
											backgroundColor: tema.bgSidebar,
											borderColor: tema.borderSeparator,
										}}
									>
										{anexos.map((a, idx) => (
											<div
												key={idx}
												className="relative overflow-hidden rounded-md border"
												style={{
													borderColor: tema.borderSeparator,
													backgroundColor: tema.chipAnexoBg,
												}}
											>
												{a.preview ? (
													<img
														src={a.preview}
														alt=""
														className="h-16 w-16 object-cover"
													/>
												) : (
													<div className="flex h-16 w-16 items-center justify-center">
														{iconeDoAnexo(
															tipoDeMidia(a.file.type || a.file.name),
															"h-6 w-6"
														)}
													</div>
												)}
												<Button
													variant="ghost"
													size="icon"
													className="absolute right-0 top-0 h-5 w-5 rounded-full"
													onClick={() => removerAnexo(idx)}
													// X preto normal, legível em prévia clara
													style={{ color: "black", backgroundColor: "rgba(255,255,255,0.55)" }}
													aria-label="Remover anexo"
												>
													<X className="h-3 w-3" />
												</Button>
												<p
													className="absolute bottom-0 left-0 right-0 truncate px-1 text-[10px] leading-tight bg-black/60 text-white"
													title={a.file.name}
												>
													{a.file.name}
												</p>
											</div>
										))}
									</div>
								)}

								{/* FOOTER INPUT — SHRINK-0, sempre mesmo tamanho */}
								<div
									className="flex items-end gap-2 border-t px-2 py-2 shrink-0"
									style={{
										backgroundColor: tema.bgInput,
										borderColor: tema.borderSeparator,
									}}
								>
									<input
										ref={fileRef}
										type="file"
										multiple
										className="hidden"
										accept="image/*,video/*,audio/*,application/*"
										onChange={(e) => {
											adicionarAnexos(e.target.files);
											e.target.value = "";
										}}
									/>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9 shrink-0"
										onClick={() => fileRef.current?.click?.()}
										style={{ color: tema.textoMuted }}
										aria-label="Anexar arquivo"
										title="Anexar arquivo"
										disabled={gravando}
									>
										<Paperclip className="h-5 w-5" />
									</Button>
									{!gravando ? (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 shrink-0"
											title="Figurinhas"
											aria-label="Figurinhas"
											onClick={() => {
												setFigurinhaAberto((a) => !a);
											}}
											ref={figurinhaAncoraRef}
										>
											<Sticker
												className="h-5 w-5"
												style={{ color: figurinhaAberto ? "#008069" : tema.textoMuted }}
											/>
										</Button>
									) : null}
									{!gravando && !texto.trim() && anexos.length === 0 ? (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 shrink-0"
											title="Gravar mensagem de áudio (clique para começar)"
											onClick={handleStartRecordW}
										>
											<Mic className="h-5 w-5" style={{ color: tema.textoMuted }} />
										</Button>
									) : null}
									{gravando ? (
										<div
											className="flex h-9 flex-1 items-center justify-between rounded-lg border-0 px-3 shadow-none"
											style={{ backgroundColor: "#ffffff", borderRadius: "8px" }}
										>
											<div className="flex items-center gap-2 min-w-0">
												<RecordingTimer />
											</div>
											<div className="flex items-center gap-1 shrink-0">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0"
													title="Cancelar gravação"
													onClick={handleCancelAudioW}
												>
													<X className="h-5 w-5 text-red-500" />
												</Button>
												<Button
													size="icon"
													className="h-8 w-8 shrink-0 border-0"
													style={{ backgroundColor: tema.verdeWpp, color: "#ffffff" }}
													title="Enviar áudio"
													onClick={handleUploadAudioW}
												>
													<Check className="h-5 w-5" />
												</Button>
											</div>
										</div>
									) : (
										<div className="min-w-0 flex-1">
											<textarea
												ref={inputRef}
												rows={1}
												value={texto}
												onChange={(e) => setTexto(e.target.value)}
												onKeyDown={handleKeyDown}
												onPaste={handlePaste}
												placeholder="Digite uma mensagem ou cole uma imagem (Ctrl+V)"
												className="w-full resize-none rounded-lg border px-3 py-2 text-sm focus:outline-hidden"
												style={{
													backgroundColor: tema.inputBg,
													borderColor: tema.borderSeparator,
													color: tema.corTextoRecebida,
													minHeight: 36,
													maxHeight: 120,
												}}
											/>
										</div>
									)}
									{!gravando ? (
										<>
											<AssistenteDeTexto
												ticketId={null}
												texto={texto}
												aoTrocarTexto={setTexto}
												desabilitado={enviando}
											/>
											<Button
												variant="ghost"
												size="icon"
												className="h-9 w-9 shrink-0"
												onClick={enviarMensagem}
												disabled={
													enviando || (!texto.trim() && anexos.length === 0)
												}
												aria-label="Enviar"
												style={{ color: tema.verdeWpp }}
												title={
													(!texto.trim() && anexos.length === 0)
														? "Digite algo para enviar"
														: "Enviar (Enter)"
												}
											>
												<Send className="h-5 w-5" />
											</Button>
										</>
									) : null}
								</div>
							</>
						)}
						{/* Figurinhas do InternalChat — posicionado com ancoraRef no botão Sticker.
						    Usa createPortal do FigurinhaPicker, então não importa a profundidade
						    da DOM aqui — ele sempre aparece sobre tudo, alinhado ao botão. */}
						<FigurinhaPicker
							aberto={figurinhaAberto && !!chatAbertoCom}
							aoFechar={() => setFigurinhaAberto(false)}
							ticketId={null /* internal chat não usa rota ticket standard */}
							ancoraRef={figurinhaAncoraRef}
							quotedMsg={quotedMessage}
							customOnSelectSticker={(figurinha, ctx) =>
								enviarFigurinhaChatInterno(figurinha, ctx)
							}
						/>
					</div>
				</div>
			)}

			{/* =========================================================
			    (F3) POPUP de reações / Responder — createPortal em document.body
			    Mesma UX da página /internal-chat.
			    ========================================================= */}
			{(function () {
				const msgId = reactionPopoverFor;
				if (msgId == null) return null;
				const mensagem = messagesRef.current.find((m) => +m.id === +msgId);
				if (!mensagem) return null;
				const balaoInfo = balaoRefs.current.get(msgId) || null;
				const balaoEl = balaoInfo?.el;
				if (!balaoEl) return null;
				const rect = balaoEl.getBoundingClientRect();
				const ehMinha = balaoInfo?.isMine ?? Number(mensagem.fromUserId) === Number(user?.id);
				// POSICIONAMENTO FIXED (relativo à VIEWPORT, não documento):
				// NÃO SOMAR window.scrollX/Y a rect, pois rect já é viewport relative
				// e o portal usa position: fixed (viewport também).
				const POPUP_W = 336;
				const POPUP_H = 56; // altura do quick bar (emojis) antes do editor
				const MARGIN = 12;
				const viewW = typeof window !== "undefined" ? window.innerWidth : 1200;
				const viewH = typeof window !== "undefined" ? window.innerHeight : 900;
				let leftPx = ehMinha
					? Math.max(MARGIN, rect.right - 40)
					: Math.max(MARGIN, rect.left + 8);
				// Clamp ESQUERDA (não sair pela esquerda) + DIREITA (não sair pela direita viewport)
				leftPx = Math.max(MARGIN, Math.min(leftPx, viewW - POPUP_W - MARGIN));
				// Tentar posicionar ACIMA do balão; se NÃO couber (topo estoura viewport), colocar ABAIXO
				const topAcima = rect.top - POPUP_H - 6;
				let topPx = topAcima >= MARGIN
					? Math.max(MARGIN, topAcima)
					: Math.min(viewH - POPUP_H - MARGIN, rect.bottom + 8);
				// #region debug-point widget_popup_pos_1
				dbgPushW({
					ev: "widget_popup_pos_calc",
					ehMinha,
					rectL: Math.round(rect.left),
					rectR: Math.round(rect.right),
					rectT: Math.round(rect.top),
					rectW: Math.round(rect.width),
					winW: viewW,
					winH: viewH,
					leftPx: Math.round(leftPx),
					topPx: Math.round(topPx),
					estouraDir: Math.round(leftPx + POPUP_W) > viewW - MARGIN ? 1 : 0,
					estouraTop: topPx < MARGIN ? 1 : 0,
				});
				// #endregion
				return createPortal(
					<div
						ref={popupHostRef}
						data-popup-host-internal-chat-widget
						data-reaction-popover
						data-skip-react
						style={{
							position: "fixed",
							top: topPx,
							left: leftPx,
							zIndex: 999999,
						}}
						onContextMenu={(e) => e.preventDefault()}
					>
						<div
							data-reaction-popover
							data-skip-react
							className="flex items-center gap-0.5 rounded-xl border px-1.5 py-1 shadow-xl"
							style={{
								backgroundColor: tema.popoverBg || "#ffffff",
								borderColor: tema.popoverBorder || "#e9edef",
							}}
						>
							{EMOJIS_RAPIDOS_W.map((item) => (
								<button
									key={item.emoji}
									type="button"
									data-skip-react
									title={item.rotulo}
									className="h-9 w-9 rounded-lg text-[22px] leading-none transition-transform hover:scale-110 hover:bg-black/5 active:scale-95"
									onClick={(e) => {
										dbgPushW({ ev: "widget_onPick_quick", emoji: item.emoji, msgId: String(msgId) });
										reactionPopoverForRef.current = null;
										toggleReaction(+msgId, item.emoji);
									}}
								>
									{item.emoji}
								</button>
							))}
							<div
								className="mx-1 h-6 w-px"
								style={{ backgroundColor: tema.separator || "#e9edef" }}
								aria-hidden
							/>
							<button
								type="button"
								data-skip-react
								title="Responder"
								className="h-9 w-9 rounded-lg transition-colors hover:bg-black/5 active:bg-black/10"
								onClick={(e) => {
									dbgPushW({ ev: "widget_onReply_click", msgId: String(msgId) });
									reactionPopoverForRef.current = null;
									replyToMessage(mensagem);
								}}
							>
								<Reply className="mx-auto h-4 w-4" style={{ color: tema.textoMuted }} />
							</button>
							<button
								type="button"
								data-skip-react
								title="Fechar"
								className="h-9 w-9 rounded-lg transition-colors hover:bg-black/5 active:bg-black/10"
								onClick={(e) => {
									dbgPushW({ ev: "widget_onClose_click", msgId: String(msgId) });
									fecharPopupReacoes();
								}}
							>
								<X className="mx-auto h-4 w-4" style={{ color: tema.textoMuted }} />
							</button>
						</div>
					</div>,
					document.body
				);
			})()}
			{imagemModalWidget &&
				typeof document !== "undefined" &&
				createPortal(
					<div
						data-skip-react
						className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
						onClick={(e) => {
							if (e.target === e.currentTarget) setImagemModalWidget(null);
						}}
						role="dialog"
						aria-modal="true"
						aria-label={imagemModalWidget.nome || "Imagem"}
					>
						<button
							type="button"
							aria-label="Fechar pré-visualização"
							className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
							onClick={(e) => {
								e.stopPropagation();
								setImagemModalWidget(null);
							}}
						>
							<X className="h-5 w-5" />
						</button>
						<div className="relative max-h-full max-w-full overflow-hidden rounded-md">
							<img
								src={imagemModalWidget.url}
								alt={imagemModalWidget.nome || "Imagem enviada"}
								className="max-h-[90vh] max-w-[92vw] rounded-md object-contain"
							/>
						</div>
						<button
							type="button"
							aria-label="Abrir original em nova aba"
							className="absolute bottom-4 right-4 inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-4 text-sm text-white backdrop-blur hover:bg-white/20"
							onClick={(e) => {
								e.stopPropagation();
								window.open(imagemModalWidget.url, "_blank", "noopener,noreferrer");
							}}
						>
							Abrir original
						</button>
					</div>,
					document.body
				)}
			<VisualizadorDeArquivo
				open={visualizandoW !== null}
				onClose={() => setVisualizandoW(null)}
				arquivos={arquivosDaConversaW}
				indice={visualizandoW ?? 0}
				onIndice={setVisualizandoW}
			/>
		</>
	);
}

export { getInitials };
